import { useEffect, useState } from 'react'
import Alert from '@mui/material/Alert'
import Button from '@mui/material/Button'
import Dialog from '@mui/material/Dialog'
import DialogContent from '@mui/material/DialogContent'
import DialogTitle from '@mui/material/DialogTitle'
import FormControl from '@mui/material/FormControl'
import IconButton from '@mui/material/IconButton'
import InputLabel from '@mui/material/InputLabel'
import LinearProgress from '@mui/material/LinearProgress'
import MenuItem from '@mui/material/MenuItem'
import OutlinedInput from '@mui/material/OutlinedInput'
import Select from '@mui/material/Select'
import Snackbar from '@mui/material/Snackbar'
import CloseIcon from '@mui/icons-material/Close'
import { useTranslation } from 'react-i18next'
import {
  useAddChannelMutation,
  useEditChannelMutation,
  useGetChannelsListQuery
} from '@/api/channels'
import { useGetBuildingsQuery } from '@/api'

const DialogForm = ({ 
  open = false, 
  onClose = () => {}, 
  currentId 
}) => {
  const { t } = useTranslation()
  const [createChannel, { isSuccess, isLoading }] = useAddChannelMutation()
  const [editChannel, { isSuccess: editSuccess, isLoading: editLoading }] =
    useEditChannelMutation()
  const { data: channels } = useGetChannelsListQuery()
  const { data: buildings } = useGetBuildingsQuery()
  const [openSnackbar, setOpenSnackbar] = useState(false)
  const [form, setForm] = useState({
    name: '',
    isActive: 0,
    buildingId: null,
    type: ''
  })

  const channel = channels?.find((c) => c?.id === currentId)

  const handleChange = ({ target }) => {
    if (target.name === 'isActive') {
      setForm({ ...form, isActive: !form.isActive })
    } else {
      setForm({ ...form, [target.name]: target.value })
    }
  }

  const handleClose = () => {
    setForm({
      name: '',
      isActive: 0,
      buildingId: null,
      type: ''
    })
    onClose()
  }

  const handleSnackOpen = () => {
    setOpenSnackbar(true)
  }

  const handleSnackClose = (event, reason) => {
    if (reason === 'clickaway') {
      return
    }

    setOpenSnackbar(false)
  }

  const onSuccess = () => {
    setForm({
      name: '',
      isActive: 0,
      buildingId: null,
      type: ''
    })
    onClose()
    handleSnackOpen()
  }

  const handleSubmit = async () => {
    if (currentId) {
      try {
        await editChannel({
          id: channel?.id,
          building_id: form.buildingId,
          name: form.name,
          is_active: Boolean(form.isActive),
          metadata: null,
          type: form.type
        }).unwrap()
        onSuccess()
      } catch (err) {
        console.log(err)
      }
    } else {
      try {
        await createChannel({
          building_id: form.buildingId,
          name: form.name,
          is_active: Boolean(form.isActive),
          metadata: null,
          type: form.type
        }).unwrap()
        onSuccess()
      } catch (err) {
        console.log(err)
      }
    }
  }

  useEffect(() => {
    if (channel) {
      setForm({
        name: channel?.name,
        isActive: channel?.is_active,
        buildingId: channel?.building_id,
        type: channel?.type
      })
    }
  }, [channel])

  return (
    <>
      <Dialog open={open} fullWidth onClose={handleClose}>
        {(isLoading || editLoading) && <LinearProgress />}
        <DialogTitle>
          {currentId ? t('editing_a_channel') : t('creating_a_channel')}
          <IconButton
            onClick={onClose}
            sx={{
              position: 'absolute',
              right: 8,
              top: 8,
              color: (theme) => theme.palette.grey[500]
            }}
          >
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        <DialogContent dividers>
          <FormControl fullWidth sx={{ mb: 3, mt: 1 }}>
            <InputLabel>{t('accesses_name')}</InputLabel>
            <OutlinedInput
              name="name"
              value={form.name}
              onChange={handleChange}
              label={t('accesses_name')}
            />
          </FormControl>
          <FormControl fullWidth sx={{ mb: 3, mt: 1 }}>
            <InputLabel>{t('enter_type')}</InputLabel>
            <Select
              name="type"
              labelId="type"
              label={t('enter_type')}
              value={form.type || ''}
              onChange={handleChange}
            >
              <MenuItem value="ANPR" key="type">
                ANPR
              </MenuItem>
              <MenuItem value="PERSON" key="type">
                PERSON
              </MenuItem>
            </Select>
          </FormControl>
          <FormControl fullWidth sx={{ mb: 3, mt: 1 }}>
            <InputLabel id="buildingId">{t('select_an_object')}</InputLabel>
            <Select
              name="buildingId"
              labelId="buildingId"
              label={t('select_an_object')}
              value={form.buildingId || ''}
              onChange={handleChange}
            >
              {buildings &&
                buildings.map((building) => (
                  <MenuItem value={building?.id} key={building?.id}>
                    {building?.name}
                  </MenuItem>
                ))}
            </Select>
          </FormControl>
          <FormControl fullWidth sx={{ mt: 3 }}>
            <InputLabel>{t('select_status')}</InputLabel>
            <Select
              labelId="isActive"
              id="isActive"
              value={form.isActive ? 1 : 0}
              label={t('select_status')}
              name="isActive"
              onChange={handleChange}
            >
              <MenuItem value={1}>{t('active')}</MenuItem>
              <MenuItem value={0}>{t('inactive')}</MenuItem>
            </Select>
          </FormControl>
          <Button
            sx={{ mt: 3 }}
            fullWidth
            variant="contained"
            onClick={handleSubmit}
            disabled={!form.name || !form.type}
          >
            {t('save')}
          </Button>
        </DialogContent>
      </Dialog>
      <Snackbar
        open={openSnackbar}
        autoHideDuration={6000}
        onClose={handleSnackClose}
      >
        <Alert
          sx={{ width: '100%' }}
          severity={isSuccess || editSuccess ? 'success' : 'error'}
          variant="filled"
          onClose={handleSnackClose}
        >
          {isSuccess || editSuccess
            ? t('change_saved!')
            : t('something_went_wrong')}
        </Alert>
      </Snackbar>
    </>
  )
}

// DialogForm.propTypes = {
//   currentId: PropTypes.string.isRequired,
//   open: PropTypes.bool,
//   onClose: PropTypes.func
// }

export default DialogForm
